import { useState, useCallback } from "react";

export interface UseMultiSelectReturn<T> {
  selectedItems: T[];
  isSelected: (item: T) => boolean;
  toggleItem: (item: T) => void;
  selectAll: (items: T[]) => void;
  clearSelection: () => void;
  isAllSelected: (items: T[]) => boolean;
  hasSelection: boolean;
}

export function useMultiSelect<T>(
  getItemId: (item: T) => string | number
): UseMultiSelectReturn<T> {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const isSelected = useCallback(
    (item: T) => {
      const itemId = getItemId(item);
      return selectedItems.some((selected) => getItemId(selected) === itemId);
    },
    [selectedItems, getItemId]
  );

  const toggleItem = useCallback(
    (item: T) => {
      const itemId = getItemId(item);
      setSelectedItems((prev) => {
        const isCurrentlySelected = prev.some(
          (selected) => getItemId(selected) === itemId
        );

        if (isCurrentlySelected) {
          return prev.filter((selected) => getItemId(selected) !== itemId);
        } else {
          return [...prev, item];
        }
      });
    },
    [getItemId]
  );

  const selectAll = useCallback((items: T[]) => {
    setSelectedItems(items);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const isAllSelected = useCallback(
    (items: T[]) => {
      if (items.length === 0) return false;
      return items.every((item) => isSelected(item));
    },
    [isSelected]
  );

  const hasSelection = selectedItems.length > 0;

  return {
    selectedItems,
    isSelected,
    toggleItem,
    selectAll,
    clearSelection,
    isAllSelected,
    hasSelection,
  };
} 